import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { UserService } from '../../core/services/core.user.service';
import { UiService } from '../../core/services/core.ui.service';
import { GfTranslateService } from '../../core/services/core.translate.service';

export interface Bookmark {
    id: string;
    title: string;
    url: string;
    section: string;
}

@Injectable()
export class FavoritesService {
    private favorites: Bookmark[] = [];
    private readonly PREFERENCE_NAME = 'user-favorites';
    private initialized = false;

    // Observable for components to subscribe to changes
    favoritesChanged$: Subject<void> = new Subject<void>();

    // Icon mapping for different section types
    private readonly sectionIconMap = {
        'trackeritem': 'glyph-img-ticket',
        'tracker': 'glyph-img-ticket-2',
        'wiki': 'glyph-img-wiki',
        'frs': 'glyph-img-release',
        'docman': 'glyph-img-document',
        'forum': 'glyph-img-chat',
        'scmgit': 'glyph-img-git',
        'scmsvn': 'glyph-img-svn',
        'scm': 'glyph-img-code',
        'project': 'glyph-img-project',
        'sprint': 'glyph-img-sprint',
        'standup': 'glyph-img-standup',
        'board': 'glyph-img-ticket-2',
        'build': 'glyph-img-cicd',
        'report': 'glyph-img-report',
        'page': 'glyph-img-star'
    };

    constructor(
        private userService: UserService,
        private uiService: UiService,
        private translateService: GfTranslateService
    ) {}

    /**
     * Initialize the service by loading favorites from user preferences
     */
    init(): Promise<void> {
        if (this.initialized) {
            return Promise.resolve();
        }

        return this.loadFavorites().then(() => {
            this.initialized = true;
        });
    }

    /**
     * Load favorites from user preferences
     */
    loadFavorites(): Promise<Bookmark[]> {
        return this.userService.preference.getPreference(this.PREFERENCE_NAME, [])
            .then((result) => {
                const value = (result && result.preferenceValue) || [];
                // Handle both array and JSON string
                if (typeof value === 'string') {
                    try {
                        this.favorites = JSON.parse(value);
                    } catch (e) {
                        this.favorites = [];
                    }
                } else if (Array.isArray(value)) {
                    this.favorites = value;
                } else {
                    this.favorites = [];
                }
                return this.favorites;
            })
            .catch(() => {
                this.favorites = [];
                return this.favorites;
            });
    }

    /**
     * Save favorites to user preferences
     */
    saveFavorites(): Promise<void> {
        return this.userService.preference.setPreference(this.PREFERENCE_NAME, this.favorites)
            .then(() => {
                this.favoritesChanged$.next();
            });
    }

    /**
     * Add a bookmark to favorites
     */
    addFavorite(bookmark: Bookmark): Promise<boolean> {
        // Check if URL is already bookmarked
        if (this.isUrlBookmarked(bookmark.url)) {
            return Promise.resolve(false);
        }

        this.favorites.unshift(bookmark);
        return this.saveFavorites()
            .then(() => {
                this.uiService.message.showSuccess(
                    this.translateService.translate('This page has been added to your favorites'),
                    this.translateService.translate('Favorites')
                );
                return true;
            })
            .catch((err) => {
                // Rollback
                this.favorites = this.favorites.filter(f => f.id !== bookmark.id);
                return false;
            });
    }

    /**
     * Remove bookmark by ID
     */
    removeFavorite(id: string): Promise<boolean> {
        const index = this.favorites.findIndex(f => f.id === id);
        if (index === -1) {
            return Promise.resolve(false);
        }

        const removed = this.favorites.splice(index, 1)[0];
        return this.saveFavorites()
            .then(() => {
                this.uiService.message.showSuccess(
                    this.translateService.translate('This page has been removed from your favorites'),
                    this.translateService.translate('Favorites')
                );
                return true;
            })
            .catch(() => {
                // Rollback
                this.favorites.splice(index, 0, removed);
                return false;
            });
    }

    /**
     * Remove favorite by URL (for toggle functionality)
     */
    removeFavoriteByUrl(url: string): Promise<boolean> {
        const normalized = this.normalizeUrl(url);
        const bookmark = this.favorites.find(f => this.normalizeUrl(f.url) === normalized);
        if (!bookmark) {
            return Promise.resolve(false);
        }
        return this.removeFavorite(bookmark.id);
    }

    /**
     * Check if a URL is bookmarked
     */
    isUrlBookmarked(url: string): boolean {
        const normalized = this.normalizeUrl(url);
        return this.favorites.some(f => this.normalizeUrl(f.url) === normalized);
    }

    /**
     * Normalize a URL by stripping trailing slashes and query strings
     */
    private normalizeUrl(url: string): string {
        return url.split('?')[0].replace(/\/+$/, '');
    }

    /**
     * Create bookmark from provided URL and context
     * @param section Section type (trackeritem, wiki, frs, etc.)
     * @param title Optional title override
     * @param url URL to bookmark (from ui-router state)
     */
    createBookmark(section: string, title?: string, url?: string): Bookmark {
        return {
            id: this.generateId(),
            title: title || 'Untitled',
            url: url || window.location.pathname + window.location.search,
            section: section
        };
    }

    /**
     * Generate unique ID for bookmark
     */
    private generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Get icon class for a section type
     */
    getSectionIcon(section: string): string {
        return this.sectionIconMap[section] || 'glyph-img-star';
    }

    /**
     * Update bookmark (e.g., rename)
     */
    updateFavorite(id: string, updates: Partial<Bookmark>): Promise<boolean> {
        const index = this.favorites.findIndex(f => f.id === id);
        if (index === -1) {
            return Promise.resolve(false);
        }

        const original = { ...this.favorites[index] };
        this.favorites[index] = { ...this.favorites[index], ...updates };

        return this.saveFavorites()
            .then(() => true)
            .catch(() => {
                // Rollback
                this.favorites[index] = original;
                return false;
            });
    }

    /**
     * Reorder favorites
     */
    reorderFavorites(orderedIds: string[]): Promise<boolean> {
        const reordered: Bookmark[] = [];
        for (const id of orderedIds) {
            const bookmark = this.favorites.find(f => f.id === id);
            if (bookmark) {
                reordered.push(bookmark);
            }
        }

        // Add any that weren't in orderedIds
        for (const bookmark of this.favorites) {
            if (!reordered.includes(bookmark)) {
                reordered.push(bookmark);
            }
        }

        const original = [...this.favorites];
        this.favorites = reordered;

        return this.saveFavorites()
            .then(() => true)
            .catch(() => {
                this.favorites = original;
                return false;
            });
    }

    /**
     * Get all favorites
     */
    getFavorites(): Bookmark[] {
        return this.favorites;
    }

    /**
     * Get favorites count
     */
    getFavoritesCount(): number {
        return this.favorites.length;
    }

    /**
     * Clear all favorites
     */
    clearFavorites(): Promise<void> {
        this.favorites = [];
        return this.userService.preference.deletePreference(this.PREFERENCE_NAME)
            .then(() => {
                this.favoritesChanged$.next();
            });
    }
}
